/**
 * Copy text to the clipboard with a legacy fallback. Returns `true` if
 * the copy succeeded, `false` otherwise; callers use the boolean to
 * flash a "Copied" / "Copy failed" affordance.
 *
 * Modern path: `navigator.clipboard.writeText` (requires secure context).
 * Fallback: a hidden `<textarea>` plus `document.execCommand("copy")`,
 * deprecated but still working in browsers that block the clipboard API
 * and in jsdom-stubbed test contexts.
 */
export async function copyText(value: string): Promise<boolean> {
  const clipboard =
    typeof navigator !== "undefined"
      ? (navigator as Navigator & { clipboard?: { writeText: (s: string) => Promise<void> } })
          .clipboard
      : undefined;

  if (clipboard?.writeText) {
    try {
      await clipboard.writeText(value);
      return true;
    } catch {
      // Fall through to execCommand fallback.
    }
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}
